import { ActionIcon, Button, CopyButton, Divider, Image, Stack, Text, Title, Tooltip } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Check, CircleCheck, Copy } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  getListTokensQueryKey,
  useCreateToken,
} from "../../api/generated/endpoints/identity/identity";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { Soon } from "../../ui/Soon";
import {
  CLIENTS,
  PROMPTS,
  STEPS,
  SUCCESS_NOTE,
  TOKEN_DAYS,
  TOKEN_PLACEHOLDER,
  claudeCodeCommand,
  codexConfig,
  codexToken,
  type Client,
  type ClientId,
} from "./clients";
import classes from "./McpServer.module.css";

/** The MCP endpoint: the api's own `/api/mcp`, on this app's origin, as every API call is. */
export function serverUrl(): string {
  return `${window.location.origin}/api/mcp`;
}

/**
 * Live's MCP Server page, "Connect your AI tool" (spec platform/mcp-server, PR 4): pick a tool,
 * and the panel says how to connect it. A tool signs in with an access token of this team, made
 * here for it: the server never takes a URL as its only credential (decision 12).
 */
export function McpServerScreen() {
  const { teamId } = useParams({ from: "/signed-in/teams/$teamId/mcp-server" });
  const [active, setActive] = useState<ClientId>("claude-code");
  const client = CLIENTS.find((each) => each.id === active) ?? CLIENTS[0];
  return (
    <div className={classes.page}>
      <Stack gap={12}>
        <Title order={1} fz={24} fw={700}>
          Connect your AI tool
        </Title>
        <Text className={classes.description}>
          Pick your AI tool and connect to the created.ai hub. It works as you, with your role in
          this team, signed in by an access token you make here.
        </Text>
      </Stack>
      <div className={classes.content}>
        <ul className={classes.list} aria-label="AI tools">
          {CLIENTS.map((each) => (
            <ClientCard
              key={each.id}
              client={each}
              active={each.id === active}
              onSelect={() => setActive(each.id)}
            />
          ))}
        </ul>
        {/* A new tool, a new panel: a token made for one never shows under another. */}
        {client === undefined ? null : <ConnectionPanel key={client.id} client={client} teamId={teamId} />}
      </div>
    </div>
  );
}

function ClientIcon({ client, active }: { client: Client; active: boolean }) {
  if (!active) return <Image src={client.icon} alt="" className={classes.icon} />;
  const dark = client.activeIconDark ?? client.activeIcon;
  return (
    <>
      <Image src={client.activeIcon} alt="" className={classes.icon} darkHidden />
      <Image src={dark} alt="" className={classes.icon} lightHidden />
    </>
  );
}

function ClientCard(props: { client: Client; active: boolean; onSelect: () => void }) {
  const { client, active, onSelect } = props;
  const body = (
    <>
      <span className={classes.iconBox}>
        <ClientIcon client={client} active={active} />
      </span>
      <span>
        <Text fw={600}>{client.label}</Text>
        <Text className={classes.where}>{client.where}</Text>
      </span>
    </>
  );
  if (client.soon) {
    // Claude.ai and ChatGPT sign in by OAuth, which is not built: their card says so.
    return (
      <li>
        <Soon block>
          {(soon) => (
            <button type="button" className={classes.card} {...soon}>
              {body}
            </button>
          )}
        </Soon>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        className={classes.card}
        data-active={active || undefined}
        aria-pressed={active}
        onClick={onSelect}
      >
        {body}
      </button>
    </li>
  );
}

function CopyBlock({ text, label }: { text: string; label: string }) {
  return (
    <div className={classes.copy}>
      <pre className={classes.copyText} aria-label={label}>
        {text}
      </pre>
      <CopyButton value={text}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy"}>
            <ActionIcon variant="subtle" onClick={copy} aria-label={`Copy ${label}`}>
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </div>
  );
}

function Step({ number, children }: { number: number; children: ReactNode }) {
  return (
    <div className={classes.step}>
      <span className={classes.badge}>{number}</span>
      <Text size="sm">{children}</Text>
    </div>
  );
}

/**
 * The tool's own token, made once on this page, in the team of the URL. Settings' Access tokens
 * list reads again once it is made, as its own form does, so the token is there to revoke.
 */
function useToolToken(teamId: string, client: Client) {
  const queryClient = useQueryClient();
  const create = useCreateToken({
    mutation: {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListTokensQueryKey() }),
    },
  });
  const make = () =>
    create.mutate({
      teamId,
      data: { name: client.label, scopes: ["read", "write"], expires_in_days: TOKEN_DAYS },
    });
  return { token: create.data?.token, make, pending: create.isPending, error: create.error };
}

function ConnectionPanel({ client, teamId }: { client: Client; teamId: string }) {
  const url = serverUrl();
  const made = useToolToken(teamId, client);
  const token = made.token ?? TOKEN_PLACEHOLDER;
  const tokenStep = (
    <Stack gap={8}>
      <Step number={1}>{STEPS.makeToken(client.label)}</Step>
      {made.token === undefined ? (
        <Button variant="light" w="fit-content" loading={made.pending} onClick={made.make}>
          Make a token
        </Button>
      ) : (
        <Text size="sm" c="var(--cai-color-text-muted)">
          Made: it is in the commands below. Copy them now; it never shows again.
        </Text>
      )}
      {made.error ? <ErrorAlert error={made.error} /> : null}
    </Stack>
  );
  return (
    <section className={classes.panel} aria-label={`Connect ${client.label}`}>
      <div className={classes.hub}>
        <span className={classes.hubLogo}>
          {/* The app's mark, as live's hub shows its own. */}
          <Image src="/created-ai-fav.png" alt="" w={28} h={28} />
        </span>
        <Stack gap={4}>
          <Text fw={700}>Created.ai Hub</Text>
          <Text size="sm" c="var(--cai-color-text-muted)">
            One server, every tool — access follows your account
          </Text>
        </Stack>
      </div>
      <Divider />
      <Stack gap={12}>
        <Text className={classes.sectionTitle}>Server URL</Text>
        <CopyBlock text={url} label="Server URL" />
      </Stack>
      <Stack gap={16}>
        {tokenStep}
        {client.id === "codex" ? (
          <>
            <Step number={2}>{STEPS.codex.addServer}</Step>
            <CopyBlock text={codexConfig(url)} label="Codex config" />
            <Step number={3}>{STEPS.codex.setToken}</Step>
            <CopyBlock text={codexToken(token)} label="Token variable" />
            <Step number={4}>{STEPS.codex.restart}</Step>
          </>
        ) : (
          <>
            <Step number={2}>{STEPS.claudeCode.runInTerminal}</Step>
            <CopyBlock text={claudeCodeCommand(url, token)} label="Claude Code command" />
            <Step number={3}>{STEPS.claudeCode.confirmConnected}</Step>
          </>
        )}
        <div className={classes.success}>
          <CircleCheck size={16} aria-hidden />
          {SUCCESS_NOTE}
        </div>
      </Stack>
      <Divider />
      <Stack gap={20}>
        <Text className={classes.sectionTitle}>Then Try Asking</Text>
        <div className={classes.chips}>
          {PROMPTS.map((prompt) => (
            <CopyButton key={prompt} value={prompt}>
              {({ copied, copy }) => (
                <button type="button" className={classes.chip} onClick={copy}>
                  {prompt}
                  {copied ? <Check size={14} aria-label="Copied" /> : <Copy size={14} aria-hidden />}
                </button>
              )}
            </CopyButton>
          ))}
        </div>
      </Stack>
    </section>
  );
}
